// Reports page and Security panel
// Extracted from portal.jsx — Phase 3 Batch 7

import React, { useEffect, useState } from "react";
import { isInternalUser } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { LIST_LIMIT } from "../../data/requests";
import { listAllShelters } from "../../data/shelters";
import { usePageSize } from "../../hooks/usePageSize";
import { buildItemReportTable, ITEM_REPORT_NUMERIC_COLS } from "../../services/reportItems";
import { passwordResetRedirect } from "../../utils/appUrl";
import { downloadCsv } from "../../utils/csv";
import { REQUEST_STATUSES } from "../../utils/enums";
import { ALL_STATUSES } from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { buildRequestCsvTable, exportToCSV, requestDecisionTotals } from "../../utils/helpers";
import { getSupabaseClient } from "../../utils/supabase";
import { PageNav } from "../common/PageNav.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { StandardReports } from "./StandardReports.jsx";

function ReportsPage({ requests }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fState, setFState] = useState("All");
  const [fShelter, setFShelter] = useState("All");
  const [fSalesGroup, setFSalesGroup] = useState("All");
  const [fStatus, setFStatus] = useState("All");
  // Shelter org records — sales_group lives here (not on the request), so the
  // Sales Order Group CSV filter needs them. Reports is admin-only, so the read
  // is allowed. Loaded once.
  const [shelterRows, setShelterRows] = useState([]);
  useEffect(() => {
    let active = true;
    listAllShelters().then((rows) => {
      if (active && rows) setShelterRows(rows);
    });
    return () => {
      active = false;
    };
  }, []);
  const [trendCat, setTrendCat] = useState("all"); // CSV category filter: all | fees | uniforms | shoes | laptops
  const [fDecision, setFDecision] = useState("approved"); // approved | declined | both
  // "Only requests with a matching item" (David, 5 Oct 2026 — default ON). Drops
  // requests with no item matching the Decision (e.g. Decision = Declined hides
  // requests where nothing was declined). Uses requestDecisionTotals().hasApproved,
  // which counts matching items even when their $ value is 0.
  const [onlyMatching, setOnlyMatching] = useState(true);
  // Which report: "summary" (one row per request — the original CSV) or "items"
  // (one row per Declined / Needs Info item, with the reason — 5 Oct 2026).
  const [reportType, setReportType] = useState("summary");
  const isItems = reportType === "items";
  // On-screen preview paging — the ONE shared rows-per-page (docs/list-pagination.md).
  const [pageSize] = usePageSize();
  const [page, setPage] = useState(0);

  const STATES = ["All", "QLD", "NSW", "VIC", "WA", "SA", "TAS", "NT", "ACT"];
  const STATUSES = ["All", ...ALL_STATUSES, "Part & Fully Processed"];
  const allRequests = requests || [];
  // shelter_code -> sales_group (sales_group lives on the shelters table, keyed by
  // the request's shelterCode). Powers the Sales Order Group CSV filter + its options.
  const salesGroupByCode = {};
  for (const s of shelterRows) {
    if (s.shelter_code) salesGroupByCode[s.shelter_code] = s.sales_group || "";
  }
  // The three shelter-scope dropdowns CASCADE: State → Sales Order Group → Shelter.
  // Each list is trimmed by the choices above it (and the onChange handlers reset
  // the ones below to "All"), so you can't pick an impossible combination.
  // Sales Order Group options = groups among shelters in the chosen State.
  const salesGroups = [
    "All",
    ...Array.from(
      new Set(
        shelterRows
          .filter((s) => fState === "All" || s.state === fState)
          .map((s) => s.sales_group)
          .filter(Boolean)
      )
    ).sort(),
  ];
  // Shelter options = shelters (with requests) in the chosen State AND Sales Order Group.
  const shelters = [
    "All",
    ...Array.from(
      new Set(
        allRequests
          .filter((r) => fState === "All" || r.state === fState)
          .filter(
            (r) => fSalesGroup === "All" || (salesGroupByCode[r.shelterCode] || "") === fSalesGroup
          )
          .map((r) => r.shelterName)
      )
    ).sort(),
  ];
  // csvFiltered drives the CSV EXPORT only. Scoped ONLY by the top "Filter Reports"
  // controls (date range / state / sales group / shelter / status / category). The
  // Statistics dashboard lives on a separate screen now (StatisticsPage).
  const csvFiltered = allRequests.filter((r) => {
    if (fState !== "All" && r.state !== fState) return false;
    if (fShelter !== "All" && r.shelterName !== fShelter) return false;
    if (fSalesGroup !== "All" && (salesGroupByCode[r.shelterCode] || "") !== fSalesGroup)
      return false;
    if (fStatus === "Part & Fully Processed") {
      if (
        r.status !== REQUEST_STATUSES.PART_PROCESSED &&
        r.status !== REQUEST_STATUSES.FULLY_PROCESSED
      )
        return false;
    } else if (fStatus !== "All" && r.status !== fStatus) {
      return false;
    }
    // Brisbane business day (reportDay), not the legacy UTC-baked `date`.
    const rday = r.reportDay || r.date;
    if (dateFrom && rday < dateFrom) return false;
    if (dateTo && rday > dateTo) return false;
    // Category filter (the former "Trend Category", now a CSV filter): keep only
    // requests that have at least one item in the selected category.
    if (trendCat !== "all") {
      const hasCat =
        trendCat === "fees"
          ? (r.children || []).some((c) => (c.fees || []).length > 0)
          : trendCat === "uniforms"
            ? (r.children || []).some((c) => !!c.uniforms)
            : trendCat === "shoes"
              ? (r.children || []).some((c) => !!c.shoes)
              : (r.children || []).some((c) => !!c.laptop);
      if (!hasCat) return false;
    }
    return true;
  });

  // The rows the preview AND the CSV both use — one list, so they can never disagree.
  const reportRows = onlyMatching
    ? csvFiltered.filter((r) => requestDecisionTotals(r, fDecision).hasApproved)
    : csvFiltered;
  const hiddenCount = csvFiltered.length - reportRows.length;
  // Same table the CSV is built from (header row first). The items report ignores
  // Decision + the tick box (it is always Declined & Needs Info items) but honours
  // every other filter, and the Category filter narrows the items themselves.
  const table = isItems
    ? buildItemReportTable(csvFiltered, trendCat)
    : buildRequestCsvTable(reportRows, fDecision);
  const [previewHeader, ...previewBody] = table;
  const pageCount = Math.max(1, Math.ceil(previewBody.length / pageSize));
  const safePage = Math.min(page, pageCount - 1); // clamp — size or filters can change
  const pageBody = previewBody.slice(safePage * pageSize, (safePage + 1) * pageSize);
  // Right-align the number columns. Summary: "Children" (index 5) onward.
  const isNumericCol = (i) => (isItems ? ITEM_REPORT_NUMERIC_COLS.includes(i) : i >= 5);
  // Items preview only: the Reason cell is cut to 2 lines (full text on hover) so long
  // reasons don't make rows very tall. Display only — the CSV keeps the full reason.
  const reasonCol = isItems ? previewHeader.indexOf("Reason") : -1;
  // Stable React keys: an item report can hold several rows for one ref number.
  const pageRows = [];
  const seenKeys = new Map();
  for (const cells of pageBody) {
    const base = isItems ? `${cells[0]}|${cells[7]}|${cells[10]}|${cells[11]}` : String(cells[0]);
    const n = seenKeys.get(base) || 0;
    seenKeys.set(base, n + 1);
    pageRows.push({ key: n ? `${base}#${n}` : base, cells });
  }
  const today = new Date().toISOString().slice(0, 10);
  const onExport = () =>
    isItems
      ? downloadCsv(table, `zephyr-declined-needs-info-${today}.csv`)
      : exportToCSV(reportRows, `zephyr-report-${today}.csv`, fDecision);

  const selectStyle =
    "py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

  return (
    <div style={{ padding: "24px 28px", maxWidth: "var(--page-max)", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 22,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#1A2E2B" }}>Reports</h1>
          <div style={{ fontSize: 12, color: "#607D79", marginTop: 4 }}>
            All dates are based on Brisbane (AEST) business days.
          </div>
          {/* Reports rides the shared request list, which the repo caps at LIST_LIMIT.
              Both the on-screen figures and the CSV silently stopped there with nothing
              to say so. Same pattern as the Suppliers list. Finding B5, 18 Aug 2026. */}
          {allRequests.length >= LIST_LIMIT && (
            <div style={{ fontSize: 12, color: "#8a5a00", fontWeight: 600, marginTop: 6 }}>
              Showing the most recent {LIST_LIMIT} requests — narrow the date range for a complete
              export.
            </div>
          )}
        </div>
        <button
          onClick={onExport}
          style={{
            background: "#388E3C",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 8,
            padding: "10px 20px",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Export to CSV
        </button>
      </div>

      {/* FILTERS */}
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 12,
          padding: "8px 16px",
          marginBottom: 22,
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#607D79",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            marginBottom: 12,
          }}
        >
          Filter Reports
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              FROM DATE
            </div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={selectStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              TO DATE
            </div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className={selectStyle}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              STATE
            </div>
            <select
              value={fState}
              onChange={(e) => {
                setFState(e.target.value);
                setFSalesGroup("All");
                setFShelter("All");
              }}
              className={selectStyle}
            >
              {STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              SALES ORDER GROUP
            </div>
            <select
              value={fSalesGroup}
              onChange={(e) => {
                setFSalesGroup(e.target.value);
                setFShelter("All");
              }}
              className={selectStyle}
            >
              {salesGroups.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              DFV SERVICE
            </div>
            <select
              value={fShelter}
              onChange={(e) => setFShelter(e.target.value)}
              className={selectStyle}
            >
              {shelters.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              STATUS
            </div>
            <select
              value={fStatus}
              onChange={(e) => setFStatus(e.target.value)}
              className={selectStyle}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              DECISION
            </div>
            <select
              value={fDecision}
              onChange={(e) => setFDecision(e.target.value)}
              className={`${selectStyle} disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={isItems}
              title={isItems ? "Not used by the Declined & Needs Info items report" : undefined}
            >
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              CATEGORY
            </div>
            <select
              value={trendCat}
              onChange={(e) => setTrendCat(e.target.value)}
              className={selectStyle}
            >
              <option value="all">All items</option>
              <option value="fees">Fees</option>
              <option value="uniforms">Uniforms</option>
              <option value="shoes">Shoes</option>
              <option value="laptops">Devices</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#607D79", marginBottom: 4, fontWeight: 600 }}>
              REPORT
            </div>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                setPage(0);
              }}
              className={selectStyle}
            >
              <option value="summary">Requests summary</option>
              <option value="items">Declined &amp; Needs Info items</option>
            </select>
          </div>
          <label
            className={`flex items-center gap-2 mt-[18px] text-[13px] text-zephyr-text ${
              isItems ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="checkbox"
              checked={onlyMatching}
              disabled={isItems}
              onChange={(e) => setOnlyMatching(e.target.checked)}
            />
            Only requests with a matching item
          </label>
          <button
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setFState("All");
              setFShelter("All");
              setFSalesGroup("All");
              setFStatus("All");
              setTrendCat("all");
              setFDecision("approved");
              setOnlyMatching(true);
              setReportType("summary");
              setPage(0);
            }}
            className={selectStyle}
            style={{ cursor: "pointer", marginTop: 18, color: "#607D79", fontFamily: "inherit" }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* PREVIEW — exactly what Export to CSV will download (same rows, same table). */}
      <section
        aria-label="Report preview"
        className="bg-white border border-zephyr-border rounded-[12px] px-4 py-3 mb-[22px]"
      >
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
          <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.5px]">
            Preview
          </div>
          <div className="text-[12px] text-zephyr-muted">
            {isItems
              ? `${previewBody.length} ${previewBody.length === 1 ? "item" : "items"} declined or needing info`
              : `${reportRows.length} ${reportRows.length === 1 ? "request" : "requests"}${
                  onlyMatching && hiddenCount > 0
                    ? ` · ${hiddenCount} hidden with no ${
                        fDecision === "both" ? "approved or declined" : fDecision
                      } item`
                    : ""
                }`}
          </div>
        </div>
        {previewBody.length === 0 ? (
          <div className="text-[13px] text-zephyr-muted py-3">
            {isItems
              ? "No declined or Needs Info items match these filters."
              : "No requests match these filters."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr>
                  {previewHeader.map((hd, i) => (
                    <th
                      key={hd}
                      className={`border-b border-zephyr-border px-2 py-[6px] font-semibold text-zephyr-muted whitespace-nowrap ${
                        isNumericCol(i) ? "text-right" : "text-left"
                      }`}
                    >
                      {hd}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.key}>
                    {row.cells.map((cell, i) => (
                      <td
                        key={previewHeader[i]}
                        className={`border-b border-zephyr-border px-2 py-[5px] text-zephyr-text align-top whitespace-pre-line ${
                          isNumericCol(i) ? "text-right tabular-nums" : "text-left"
                        }`}
                      >
                        {i === reasonCol && cell ? (
                          <div className="line-clamp-2 max-w-[280px]" title={cell}>
                            {cell}
                          </div>
                        ) : (
                          cell
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PageNav page={safePage} pageCount={pageCount} onPage={setPage} />
      </section>

      {/* The Statistics dashboard moved to its own screen — see StatisticsPage.jsx. */}

      <StandardReports requests={allRequests} />
    </div>
  );
}

// --- LOGIN (components extracted to src/components/auth/) --------------------
// ── SECURITY SETTINGS PANEL ───────────────────────────────────────────────────
// Shown when user clicks their name in the nav
function SecurityPanel({ onClose }) {
  // `user` now comes from SessionContext rather than a prop (Phase 3 refactor).
  const { user } = useSession();
  const [mfaStatus, setMfaStatus] = React.useState(null); // null=loading, true=enrolled, false=not enrolled
  const [showSetup, setShowSetup] = React.useState(false);
  const [showDisable, setShowDisable] = React.useState(false);
  const [disableCode, setDisableCode] = React.useState("");
  const [disableError, setDisableError] = React.useState("");
  const [disableLoading, setDisableLoading] = React.useState(false);
  const [enrollData, setEnrollData] = React.useState(null); // { qrCode, secret, factorId }
  const [enrollCode, setEnrollCode] = React.useState("");
  const [enrollError, setEnrollError] = React.useState("");
  const [enrollLoading, setEnrollLoading] = React.useState(false);
  const [savedMsg, setSavedMsg] = React.useState("");

  React.useEffect(function () {
    checkMfaStatus();
  }, []);

  async function checkMfaStatus() {
    setMfaStatus(null);
    const { data } = await getSupabaseClient().auth.mfa.listFactors();
    const enrolled = !!data?.totp?.some(function (f) {
      return f.status === "verified";
    });
    setMfaStatus(enrolled);
  }

  async function startEnroll() {
    setEnrollError("");
    setEnrollLoading(true);
    // Unenroll any unverified factors first
    const { data: existing } = await getSupabaseClient().auth.mfa.listFactors();
    if (existing?.totp) {
      for (const f of existing.totp) {
        if (f.status !== "verified") {
          await getSupabaseClient().auth.mfa.unenroll({ factorId: f.id });
        }
      }
    }
    const { data, error: err } = await getSupabaseClient().auth.mfa.enroll({
      factorType: "totp",
      issuer: "Zephyr Education",
      friendlyName: "Zephyr Portal",
    });
    setEnrollLoading(false);
    if (err) {
      setEnrollError(err.message);
      return;
    }
    setEnrollData({ qrCode: data.totp.qr_code, secret: data.totp.secret, factorId: data.id });
    setShowSetup(true);
  }

  async function confirmEnroll() {
    if (enrollCode.length !== 6) {
      setEnrollError("Please enter your 6-digit code.");
      return;
    }
    setEnrollLoading(true);
    setEnrollError("");
    const { data: challenge, error: cErr } = await getSupabaseClient().auth.mfa.challenge({
      factorId: enrollData.factorId,
    });
    if (cErr) {
      setEnrollError(cErr.message);
      setEnrollLoading(false);
      return;
    }
    const { error: vErr } = await getSupabaseClient().auth.mfa.verify({
      factorId: enrollData.factorId,
      challengeId: challenge.id,
      code: enrollCode,
    });
    setEnrollLoading(false);
    if (vErr) {
      setEnrollError("Invalid code — please try again.");
      return;
    }
    setShowSetup(false);
    setEnrollData(null);
    setEnrollCode("");
    setSavedMsg(`MFA enabled ${GLYPH.check}`);
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
    checkMfaStatus();
  }

  async function disableMfa() {
    setDisableError("");
    setDisableLoading(true);
    const { data } = await getSupabaseClient().auth.mfa.listFactors();
    const totp = data?.totp?.find(function (f) {
      return f.status === "verified";
    });
    if (!totp) {
      setDisableError("No MFA factor found.");
      setDisableLoading(false);
      return;
    }
    // Verify code first before unenrolling
    const { data: challenge, error: cErr } = await getSupabaseClient().auth.mfa.challenge({
      factorId: totp.id,
    });
    if (cErr) {
      setDisableError(cErr.message);
      setDisableLoading(false);
      return;
    }
    const { error: vErr } = await getSupabaseClient().auth.mfa.verify({
      factorId: totp.id,
      challengeId: challenge.id,
      code: disableCode,
    });
    if (vErr) {
      setDisableError("Invalid code.");
      setDisableLoading(false);
      return;
    }
    const { error: uErr } = await getSupabaseClient().auth.mfa.unenroll({ factorId: totp.id });
    setDisableLoading(false);
    if (uErr) {
      setDisableError(uErr.message);
      return;
    }
    setShowDisable(false);
    setDisableCode("");
    try {
      localStorage.removeItem(`zephyr_mfa_${user.email}`);
    } catch (_e) {}
    setSavedMsg(`MFA disabled ${GLYPH.check}`);
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
    checkMfaStatus();
  }

  // "Internal" = any non-shelter Zephyr user (includes pickers); drives the MFA
  // copy below. Named isStaff for historical reasons — see access.isInternalUser.
  const isStaff = isInternalUser(user);
  const ss = "py-2 px-3 rounded-[8px] border border-[#ccc] text-[13px] w-full box-border";

  return (
    <ModalBackdrop
      onClose={onClose}
      contentStyle={{
        background: "white",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 420,
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        maxHeight: "90vh",
        overflowY: "auto",
        top: "60px",
        left: "auto",
        right: "20px",
        transform: "none",
      }}
    >
      <div
        style={{
          padding: "18px 20px",
          borderBottom: "1px solid #eee",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#00695C" }}>Account Security</div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{user.email}</div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
            color: "#888",
          }}
        >
          {GLYPH.times}
        </button>
      </div>

      <div style={{ padding: 20 }}>
        {savedMsg && (
          <div style={{ color: "#2E7D32", fontWeight: 600, fontSize: 13, marginBottom: 12 }}>
            {savedMsg}
          </div>
        )}

        {/* Change Password */}
        <div style={{ marginBottom: 20, padding: 16, border: "1px solid #eee", borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Password</div>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>
            Send yourself a password reset email.
          </div>
          <button
            onClick={function () {
              getSupabaseClient().auth.resetPasswordForEmail(user.email, {
                redirectTo: passwordResetRedirect(),
              });
              setSavedMsg(`Password reset email sent ${GLYPH.check}`);
            }}
            style={{
              background: "none",
              border: "1px solid #ccc",
              borderRadius: 7,
              padding: "8px 16px",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
              color: "#333",
            }}
          >
            {GLYPH.email} Send Password Reset Email
          </button>
        </div>

        {/* MFA Section */}
        <div style={{ padding: 16, border: "1px solid #eee", borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
            Two-Factor Authentication (MFA)
          </div>
          {mfaStatus === null && (
            <div style={{ fontSize: 13, color: "#888" }}>Checking status...</div>
          )}
          {mfaStatus === false && !showSetup && (
            <div>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>
                MFA is not enabled.
                {isStaff ? " As Zephyr staff, MFA is required." : " Enable MFA for extra security."}
              </div>
              <button
                onClick={startEnroll}
                disabled={enrollLoading}
                style={{
                  background: "#00695C",
                  color: "white",
                  border: "none",
                  borderRadius: 7,
                  padding: "8px 16px",
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontWeight: 600,
                }}
              >
                {enrollLoading ? "Setting up..." : "Enable MFA"}
              </button>
              {enrollError && (
                <div style={{ color: "#C62828", fontSize: 12, marginTop: 8 }}>{enrollError}</div>
              )}
            </div>
          )}
          {mfaStatus === false && showSetup && enrollData && (
            <div>
              <div style={{ fontSize: 13, color: "#444", marginBottom: 12 }}>
                Scan this QR code with your authenticator app, then enter the 6-digit code to
                confirm.
              </div>
              <div style={{ textAlign: "center", marginBottom: 12 }}>
                <img
                  src={enrollData.qrCode}
                  alt="QR Code"
                  style={{ width: 160, height: 160, border: "1px solid #eee", borderRadius: 8 }}
                />
              </div>
              <div
                style={{
                  background: "#f5f5f5",
                  borderRadius: 8,
                  padding: "8px 12px",
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                  Manual entry code:
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 12, letterSpacing: 1 }}>
                  {enrollData.secret}
                </div>
              </div>
              <input
                value={enrollCode}
                onChange={function (e) {
                  setEnrollCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setEnrollError("");
                }}
                placeholder="000000"
                maxLength={6}
                // biome-ignore lint/a11y/noAutofocus: deliberate UX — focus the OTP input during MFA enrolment
                autoFocus
                className={ss}
                style={{
                  fontSize: 20,
                  textAlign: "center",
                  letterSpacing: 6,
                  marginBottom: 8,
                  fontFamily: "inherit",
                }}
              />
              {enrollError && (
                <div style={{ color: "#C62828", fontSize: 12, marginBottom: 8 }}>{enrollError}</div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={function () {
                    setShowSetup(false);
                    setEnrollData(null);
                    setEnrollCode("");
                  }}
                  style={{
                    flex: 1,
                    background: "none",
                    border: "1px solid #ccc",
                    borderRadius: 7,
                    padding: "8px",
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmEnroll}
                  disabled={enrollLoading || enrollCode.length !== 6}
                  style={{
                    flex: 2,
                    background: "#00695C",
                    color: "white",
                    border: "none",
                    borderRadius: 7,
                    padding: "8px",
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontWeight: 600,
                    opacity: enrollLoading || enrollCode.length !== 6 ? 0.6 : 1,
                  }}
                >
                  {enrollLoading ? "Verifying..." : "Confirm"}
                </button>
              </div>
            </div>
          )}
          {mfaStatus === true && !showDisable && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: "#2E7D32", fontWeight: 600 }}>
                  {GLYPH.checkGreen} MFA is enabled
                </span>
              </div>
              {!isStaff && (
                <button
                  onClick={function () {
                    setShowDisable(true);
                  }}
                  style={{
                    background: "none",
                    border: "1px solid #EF5350",
                    borderRadius: 7,
                    padding: "8px 16px",
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    color: "#C62828",
                  }}
                >
                  Disable MFA
                </button>
              )}
              {isStaff && (
                <div style={{ fontSize: 12, color: "#888" }}>
                  MFA cannot be disabled for Zephyr staff accounts.
                </div>
              )}
            </div>
          )}
          {mfaStatus === true && showDisable && (
            <div>
              <div style={{ fontSize: 13, color: "#444", marginBottom: 12 }}>
                Enter your current authenticator code to disable MFA.
              </div>
              <input
                value={disableCode}
                onChange={function (e) {
                  setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setDisableError("");
                }}
                placeholder="000000"
                maxLength={6}
                // biome-ignore lint/a11y/noAutofocus: deliberate UX — focus the OTP input when disabling MFA
                autoFocus
                className={ss}
                style={{
                  fontSize: 20,
                  textAlign: "center",
                  letterSpacing: 6,
                  marginBottom: 8,
                  fontFamily: "inherit",
                }}
              />
              {disableError && (
                <div style={{ color: "#C62828", fontSize: 12, marginBottom: 8 }}>
                  {disableError}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={function () {
                    setShowDisable(false);
                    setDisableCode("");
                  }}
                  style={{
                    flex: 1,
                    background: "none",
                    border: "1px solid #ccc",
                    borderRadius: 7,
                    padding: "8px",
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={disableMfa}
                  disabled={disableLoading || disableCode.length !== 6}
                  style={{
                    flex: 2,
                    background: "#C62828",
                    color: "white",
                    border: "none",
                    borderRadius: 7,
                    padding: "8px",
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontWeight: 600,
                    opacity: disableLoading || disableCode.length !== 6 ? 0.6 : 1,
                  }}
                >
                  {disableLoading ? "Disabling..." : "Disable MFA"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

export { ReportsPage, SecurityPanel };
